
































































































































































































































import { ForgeUser } from "@/model/ForgeUser";
import { Prop, Component, Vue, Watch } from "vue-property-decorator";
import { UIModel } from "@/UIModel";
import { FrontendBluebeamConnectProjectSettings } from "@/model/frontend/FrontendBluebeamConnectSettings";
import FolderTreeNode, { FolderNode } from "@/components/foldertreenode.vue";
import { FrontendBIM360Project } from "./../model/frontend/FrontendBIM360Project";
import cookie from 'js-cookie';
import { FrontendRegisteredWebhook } from "@/model/frontend/FrontendRegisteredWebhook";
import { ERegion } from "@/model/ERegion";
import { BackendBluebeamConnectSettings } from "@/model/backend/BackendBluebeamConnectSettings";

@Component({
    components: {
        FolderTreeNode
    }
})
export default class BluebeamConnect extends Vue {
    // Props
    @Prop({ required: true }) public model!: UIModel;

    public settings = new FrontendBluebeamConnectProjectSettings();
    public bluebeamCredentials = {
        accessToken: "",
        refreshToken: "",
        tokenType: "",
        expiresIn: 0
    };

    public autodeskCredentials = {
        accessToken: "",
        refreshToken: "",
        tokenType: "",
        expiresIn: 0
    };

    public clientID = "";
    public clientAssetID = "";
    public productCode = "";
    public bluebeamMetadataSettings: BackendBluebeamConnectSettings[] = [];
    public selectedLicense: BackendBluebeamConnectSettings | null = null;
    public configuredProjects: any[] = [];
    public selectedProject: FrontendBIM360Project | null = null;
    public loadingFolders = false;
    public inputFolders: FolderNode[] = [];
    public outputFolders: FolderNode[] = [];
    public selectedInputNode: FolderNode | null = null;
    public selectedInputNodeId: string | null = null;
    public selectedOutputNode: FolderNode | null = null;
    public selectedOutputNodeId: string | null = null;
    private pendingProjectId: string | null = null;
    public editProjectLoading: string | null = null;
    public deletingConnection: string | null = null;
    public selectedAdmins: ForgeUser[] = [];
    public usersList: ForgeUser[] = [];
    public filteredUsers: ForgeUser[] = [];

    public saving = false;

    public webhookFolders: any[] = [];
    public loadingWebhookFolders = false;
    public registeringWebhook = false;
    public registeredWebhooks: FrontendRegisteredWebhook[] = [];
    public deletingWebhookId: string | null = null;

    public bluebeamTokensMap: Record<string, boolean> = {};
    public autodeskTokensMap: Record<string, boolean> = {};
    public bluebeamConnectionLoadedMap: Record<string, boolean> = {};

    public isBluebeamConnectionLoaded = false;
    public creatingConnection = false;
    public defaultBluebeamProjectId = "";
    public loadingLicenses = true;

    @Watch("model.projects.data")
    public async onProjectsLoaded() {
        // Only proceed if loading is complete and we have data                                                                                           
        if (!this.model.projects.isLoading && this.pendingProjectId && this.model.projects.data?.length > 0) {
            const project = this.model.projects.getItemWithID(this.pendingProjectId);
            if (project && project.hubId && project.name) {
                this.selectedProject = project;
                await this.onProjectChange(project);
                this.pendingProjectId = null;
            } else {
                this.snackbar(
                    "Cannot access this project. Please ensure you have valid permissions to this BIM 360/ACC project or ask a project admin to add you.",
                    "OK",
                    "is-danger"
                );
                this.pendingProjectId = null;
            }
        }
    }

    public get hasBluebeamCredentials() {
        if (!this.selectedProject) return false;
        return !!this.bluebeamTokensMap[this.selectedProject.id];
    }

    public get hasAutodeskCredentials() {
        if (!this.selectedProject) return false;
        return !!this.autodeskTokensMap[this.selectedProject.id];
    }

    @Watch("selectedLicense")
    public async onLicenseSelected() {
        await this.loadLicenseData();
    }

    async mounted() {
        this.loadingLicenses = true;
        try {
            const productCode = String( process.env.VUE_APP_BLUEBEAM_CONNECT_PRODUCT_CODE ); 
            this.bluebeamMetadataSettings = await this.model.getBluebeamaccessSettings();
            const foundLicenses = this.model.settings.licenses.filter( license => license.product_code === productCode );
            // Create metadata from foundLicenses when it's missing so the frontend can save the settings.            
            for (const license of foundLicenses) {
                const exists = this.bluebeamMetadataSettings.some(
                    (setting) => setting.massifClientAssetId === license._id
                );
                if (!exists) {
                    const newSetting: any = {
                        isActive: true,
                        contractManager: license.contract_manager ?? "",
                        seats: license.seats ?? 0,
                        admins: [],
                        projects: [],
                        module: license.product_code ?? "",
                        id: license._id ?? "",
                        massifClientAssetId: license._id ?? "",
                        massifClientId: license.client_id ?? ""
                    };
                    this.bluebeamMetadataSettings.push(newSetting);
                }
            }

            if(this.bluebeamMetadataSettings.length == 1) {
                this.selectedLicense = this.bluebeamMetadataSettings[0];
                await this.loadLicenseData();
            }
            
            await this.loadWebhooks();
        } finally {
            this.loadingLicenses = false;
            console.log("loadingLicenses - finished");
        }
    }

    private async loadLicenseData() {
        if (!this.selectedLicense) return;

        if (this.selectedLicense.contractManager === this.model?.user?.email) {
            this.usersList = await this.model.getAccAccountUsers();
            this.filteredUsers = this.usersList;
        }

        this.clientID = String(this.selectedLicense.massifClientId);
        this.clientAssetID = String(this.selectedLicense.massifClientAssetId);
        this.productCode = String(this.selectedLicense.module);
        this.defaultBluebeamProjectId = `${crypto.randomUUID()}:${this.clientAssetID}`;

        const metadata = await this.model.getClientAssetMetadata(
            this.clientID,
            this.clientAssetID
        );

        this.configuredProjects = metadata?.projects || [];
        this.selectedAdmins = metadata?.admins;
    }

    public get isWebhookAlreadyRegisteredForProject(): boolean {
        if (!this.selectedProject) return false;
        return this.registeredWebhooks.some(
            w => w.autodeskProjectId === this.selectedProject?.id
        );
    }

    public getFilteredUsers(text: string) {
        this.filteredUsers = this.usersList.filter((user) => {
            return (
                user.name.toLowerCase().includes(text.toLowerCase()) ||
                user.email.toLowerCase().includes(text.toLowerCase())
            );
        });
    }

    public async loadWebhooks(): Promise<void> {
        if (this.model.selectedHub) {
            const resp = await this.model.getRegisteredWebhooks(this.model.selectedHub?.region as ERegion);
            console.log("resp.registeredWebhooks: ", resp.registeredWebhooks);
            this.registeredWebhooks = resp.registeredWebhooks.filter(
                w => w.autodeskAccountId == this.model.accountID &&
                    w.isBluebeamConnect &&
                    w.env == process.env.NODE_ENV
            );
            console.log("this.registeredWebhooks: ", this.registeredWebhooks);
        }
    }

    public clearSettings() {
        this.selectedProject = null;
        this.clearSettingsWhenChangingProject();
    }

    public clearSettingsWhenChangingProject() {
        this.selectedInputNodeId = null;
        this.selectedInputNode = null;
        this.selectedOutputNodeId = null;
        this.selectedOutputNode = null;
        this.settings.bluebeamProjectId = "";
        this.settings.bluebeamProjectName = "";
        this.inputFolders = [];
        this.outputFolders = [];
        this.pendingProjectId = null;
        this.isBluebeamConnectionLoaded = false;
    }

    public async deleteLogin(vendor: "bluebeam" | "forma") {
        this.$buefy.dialog.confirm({
            title: 'Delete Connection',
            message: `Are you sure you want to disconnect your ${vendor} account?`,
            type: 'is-danger',
            onConfirm: async () => {
                this.deletingConnection = vendor;
                try {
                    await this.model.deleteBluebeamAccessLogin(this.clientAssetID, this.settings.bluebeamProjectId, vendor);
                    if (this.selectedProject?.id) {
                        if (vendor === 'bluebeam') this.$set(this.bluebeamTokensMap, this.selectedProject.id, false);
                        else this.$set(this.autodeskTokensMap, this.selectedProject.id, false);
                    }
                    this.snackbar(`${vendor?.charAt(0)?.toUpperCase() + vendor?.slice(1)} connection disconnected.`, "OK", "is-success");
                } catch (err) {
                    this.snackbar(`Failed to remove ${vendor} connection.`, "OK", "is-danger");
                } finally {
                    this.deletingConnection = null;
                }
            }
        });
    }

    public async deleteBluebeamConnection(projectSettings: any) {
        this.$buefy.dialog.confirm({
            title: 'Delete Bluebeam Connection',
            message: `Are you sure you want to remove the Bluebeam connection for <b>${projectSettings.projectName}</b>?`,
            type: 'is-danger',
            onConfirm: async () => {
                try {
                    await this.model.deleteBluebeamAccessConnection(this.clientAssetID, projectSettings.bluebeamProjectId);
                    this.snackbar(`Bluebeam connection deleted successfully.`, "OK", "is-success");
                    this.isBluebeamConnectionLoaded = false;
                    if(this.selectedProject?.id) this.$set(this.bluebeamConnectionLoadedMap, this.selectedProject?.id, false);
                    await this.onLicenseSelected();
                } catch (err) {
                    this.snackbar(`Failed to delete Bluebeam connection.`, "OK", "is-danger");
                }
            }
        });
    }

    public async saveSettings() {
        if (!this.selectedInputNodeId
            || !this.selectedOutputNodeId
            || !this.model.selectedHub
            || !this.selectedProject
        ) {
            this.$buefy.snackbar.open({
                message: 'Please select Hub, Project, Input and Output folders.',
                type: 'is-warning',
                position: 'is-bottom-right'
            });
            return;
        }

       if (await this.isHubLicenseLimitReached()) {                                                                                                                      
           return;                                                                                                                                                       
       }

        this.saving = true;
        this.settings.inputFolderUrn = this.selectedInputNodeId;
        this.settings.inputFolderName = this.selectedInputNode?.name ?? "";
        this.settings.outputFolderUrn = this.selectedOutputNodeId;
        this.settings.outputFolderName = this.selectedOutputNode?.name ?? "";
        this.settings.hubId = this.model.selectedHub?.id;
        this.settings.projectId = this.selectedProject?.id;
        this.settings.projectName = this.selectedProject?.name;
        this.settings.massifClientId = this.clientID;
        this.settings.massifClientAssetId = this.clientAssetID;
        if(!this.settings.bluebeamProjectId) this.settings.bluebeamProjectId = this.defaultBluebeamProjectId;

        const metadata = {
            projects: [
                this.settings
            ],
            admins: this.selectedAdmins
        }

        try {
            await this.model.saveSettings(this.clientID, this.clientAssetID, this.productCode, metadata);
            console.log("metadata: ", metadata);
        
            await this.onLicenseSelected();
            
            this.$buefy.snackbar.open({
                indefinite: true,
                message: 'Settings saved successfully!',
                actionText: 'OK',
                type: 'is-success',
                position: 'is-bottom-right',
                queue: false
            });
        } catch (error) {
            console.error("Save failed:", error);
            this.$buefy.snackbar.open({
                indefinite: true,
                message: 'Failed to save settings.',
                type: 'is-danger',
                position: 'is-bottom-right',
                queue: false,
                onAction: () => {
                }
            });
        } finally {
            this.saving = false;
        }
    }

    private async isHubLicenseLimitReached(): Promise<boolean> {                                                                                                      
        if (!this.model.selectedHub || !this.selectedLicense) return false;                                                                                           
                                                                                                                                                                        
        const currentMetadata = await this.model.getClientAssetMetadata(this.clientID, this.clientAssetID);                                                           
        const existingProjects = currentMetadata?.projects || [];                                                                                                     
                                                                                                                                                                        
        const usedHubIds = new Set(existingProjects.map((p: any) => p.hubId));                                                                                        
        const isHubAlreadyAuthorized = usedHubIds.has(this.model.selectedHub.id);                                                                                     
                                                                                                                                                                        
        if (!isHubAlreadyAuthorized) {                                                                                                                                
            if (usedHubIds.size >= Number(this.selectedLicense.seats)) {                                                                                              
                this.snackbar(                                                                                                                                        
                    `You have reached the maximum number of hubs (${this.selectedLicense.seats}) allowed for this license.`,                                          
                    "OK",                                                                                                                                             
                    "is-danger"                                                                                                                                       
                );                                                                                                                                                    
                return true;                                                                                                                                          
            }                                                                                                                                                         
        }                                                                                                                                                             
        return false;                                                                                                                                                 
    }               

    public async saveAdmins() {
        this.saving = true;

        const metadata = {
            admins: this.selectedAdmins
        }

        try {
            await this.model.saveSettings(this.clientID, this.clientAssetID, this.productCode, metadata);
        
            await this.onLicenseSelected();
            
            this.$buefy.snackbar.open({
                indefinite: true,
                message: 'Settings saved successfully!',
                actionText: 'OK',
                type: 'is-success',
                position: 'is-bottom-right',
                queue: false
            });
        } catch (error) {
            console.error("Save failed:", error);
            this.$buefy.snackbar.open({
                indefinite: true,
                message: 'Failed to save settings.',
                type: 'is-danger',
                position: 'is-bottom-right',
                queue: false,
                onAction: () => {
                }
            });
        } finally {
            this.saving = false;
        }
    }
    private createNode(item: any): FolderNode {
        return {
            id: item.id,
            name: item.attributes.displayName || item.attributes.name,
            type: item.type === "folders" ? "folder" : "file",
            children: [],
            expanded: false,
            loading: false,
            loaded: false
        };
    }

    public async editProjectSettings(projectSettings: any) {
        this.editProjectLoading = projectSettings.projectId;
        try {
            const { inputFolderUrn, inputFolderName, outputFolderUrn, outputFolderName, projectId, bluebeamProjectId, bluebeamProjectName } = projectSettings;
            this.selectedInputNodeId = inputFolderUrn;
            this.selectedInputNode = { name: inputFolderName, id: inputFolderUrn } as FolderNode;
            this.selectedOutputNodeId = outputFolderUrn;
            this.selectedOutputNode = { name: outputFolderName, id: outputFolderUrn } as FolderNode;
            this.pendingProjectId = projectId;
            this.settings.bluebeamProjectId = bluebeamProjectId;
            this.settings.bluebeamProjectName = bluebeamProjectName;
            
            this.isBluebeamConnectionLoaded = false;
            try {
                await this.model.getBluebeamAccessConnection(this.clientAssetID, bluebeamProjectId);
                this.isBluebeamConnectionLoaded = true;
                this.$set(this.bluebeamConnectionLoadedMap, projectId, true);
            } catch (err) {
                console.log("Connection not found or error:", err);
                this.$set(this.bluebeamConnectionLoadedMap, projectId, false);
            }
            
            await this.onProjectsLoaded();

            if(bluebeamProjectId) {
                const bbTokens = await this.model.getBluebeamAccessTokens(this.clientAssetID, bluebeamProjectId, "bluebeam");
                const isBbTokenValid = this.isTokenValid(bbTokens?.accessToken);
                this.$set(this.bluebeamTokensMap, projectId, isBbTokenValid);

                const adTokens = await this.model.getBluebeamAccessTokens(this.clientAssetID, bluebeamProjectId, "forma");
                console.log("adTokens: ", adTokens);
                const isAdTokenValid = this.isTokenValid(adTokens?.accessToken);
                this.$set(this.autodeskTokensMap, projectId, isAdTokenValid);
            }

            console.log("this.bluebeamTokensMap: ", this.bluebeamTokensMap);
            console.log("this.autodeskTokensMap: ", this.autodeskTokensMap);
            console.log("this.bluebeamConnectionLoadedMap: ", this.bluebeamConnectionLoadedMap);

        } finally {
            this.editProjectLoading = null;
        }
    }

    public isTokenValid (token: string): boolean {
        try {
            const payload = token.split('.')[1];
            const decoded = JSON.parse(atob(payload));
            return decoded.exp > Math.floor(Date.now() / 1000);
        } catch (e) {
            return false;
        }
    };

    public get isProjectNew(): boolean {
        if (!this.selectedProject) return false;
        return !this.isProjectConfigured(this.selectedProject.id);
    }

    public isProjectConfigured(projectId: string): boolean {
        return this.configuredProjects.some(p => p.projectId === projectId);
    }

    public isProjectWebhookRegistered(projectId: string): boolean {
        return this.registeredWebhooks.some(w => w.autodeskProjectId === projectId);
    }

    public confirmDeleteProject(project: any) {
        if (this.isProjectWebhookRegistered(project.projectId)) {
            this.$buefy.snackbar.open({
                message: 'Please delete the registered webhook before deleting the project configuration.',
                type: 'is-warning',
                position: 'is-bottom-right'
            });
            return;
        }

        this.$buefy.dialog.confirm({
            title: 'Deleting Project Configuration',
            message: `Are you sure you want to remove the configuration for <b>${project.projectName}</b>?`,
            confirmText: 'Delete',
            type: 'is-danger',
            hasIcon: true,
            onConfirm: () => this.deleteProjectSettings(project)
        })
    }

    public async deleteProjectSettings(projectToDelete: any) {
        try {
            const metadata = await this.model.getClientAssetMetadata(this.clientID, this.clientAssetID);
            
            if (metadata && metadata.projects) {
                metadata.projects = metadata.projects.filter(
                    (p: any) => p.projectId !== projectToDelete.projectId
                );

                await this.model.saveSettings(
                    this.clientID, 
                    this.clientAssetID, 
                    this.productCode, 
                    metadata,
                    'sync'
                );

                this.configuredProjects = metadata.projects;
                this.snackbar("Project settings deleted successfully.", "OK", "is-success");
            }
        } catch (error) {
            console.error("Delete failed:", error);
            this.snackbar("Failed to delete project settings.", "OK", "is-danger");
        }
    }
    
    public async onProjectChange(project: FrontendBIM360Project) {
        try {
            if (project && project.hubId && project.id) {
                if(!this.isProjectConfigured(project.id)) {
                    this.clearSettingsWhenChangingProject();
                }
                this.loadingFolders = true;
                await this.model.getProjectFolders(project.hubId, project.id);
                const topFolders = this.model.projectFolders.data;
                if (topFolders && topFolders.length > 0) {
                    const topFolder = topFolders[0];
                    const folderContents = await this.model.getProjectFolderContents(project.id, topFolder.id);
                    this.inputFolders = folderContents.map((item: any) => this.createNode(item));
                    this.outputFolders = folderContents.map((item: any) => this.createNode(item));
                } else {
                    this.snackbar("Please make sure you have access to the required data management files.", "OK", "is-danger");
                }
                console.log(this.inputFolders, this.outputFolders);
                this.loadingFolders = false;
            }
        } catch (err) {
            console.log(err);
        }
    }

    public async onFolderToggle(
        node: FolderNode
    ): Promise<void> {
        console.log("onFolderToggle called for:", node.name, "ID:", node.id);

        if (node.expanded) {
            node.expanded = false;
            return;
        }

        if (!node.loaded && this.selectedProject?.id) {
            node.loading = true;
            try {
                const projectID = this.selectedProject.id;
                const folderContents = await this.model.getProjectFolderContents(projectID, node.id);
                node.children = folderContents.map((item: any) => ({
                    id: item.id,
                    name: item.attributes.displayName || item.attributes.name,
                    type: item.type === "folders" ? "folder" : "file",
                    children: [],
                    expanded: false,
                    loading: false,
                    loaded: false
                }));
                node.loaded = true;
            } catch (error) {
                console.error("Error fetching folder contents:", error);
            } finally {
                node.loading = false;
            }
        }

        node.expanded = true;
    }

    public onInputNodeSelect(node: FolderNode): void {
        this.selectedInputNodeId = node.id;
        this.selectedInputNode = node;
    }

    public onOutputNodeSelect(node: FolderNode): void {
        this.selectedOutputNodeId = node.id;
        this.selectedOutputNode = node;
    }

    public async registerWebhookForInputFolder(): Promise<void> {
        if (!this.selectedProject || !this.selectedInputNode || !this.model.accountID) return;
        this.registeringWebhook = true;
        try {
            const project = this.selectedProject;
            const folderUrn = this.selectedInputNode.id;
            console.log("this.selectedInputNode: ", this.selectedInputNode);
            const accountID = this.model.accountID;
            const projectID = project.id;
            const projectName = project.name;
            const folderName = this.selectedInputNode.name  || "";
            const region = this.model.selectedHub?.region as ERegion || "US";

            // Delete any existing webhook for this project before registering a new one
            const existing = this.registeredWebhooks.find(w => w.autodeskProjectId === projectID);
            if (existing) {
                console.log("Deleting existing webhook for project:", existing.hookId, "folder:", existing.folderName);
                await this.model.deleteWebhook(existing.hookId, region as ERegion);
            }

            await this.model.registerWebhookForBluebeamConnectInputFolder(
                region,
                folderUrn,
                accountID,
                projectID,
                projectName,
                folderName,
                this.clientID,
                this.clientAssetID
            );
            await this.saveSettings();
            this.snackbar("Webhook registered successfully!", "OK", "is-success");
            const resp = await this.model.getRegisteredWebhooks(this.model.selectedHub?.region as ERegion);
            this.registeredWebhooks = resp.registeredWebhooks.filter(w => w.autodeskAccountId == this.model.accountID && w.isBluebeamConnect && w.env == process.env.NODE_ENV);
        } catch (error) {
            console.error("Failed to register webhook:", error);
            this.snackbar("Failed to register webhook", "OK", "is-danger");
        } finally {
            this.registeringWebhook = false;
        }
    }

    public snackbar(message: string, actionText: string, type: string) {
        this.$buefy.snackbar.open({
            indefinite: true,
            message: message,
            actionText: actionText,
            type: type,
            position: "is-bottom-right",
            queue: false
        });
    }

    public async deleteWebhook(webhook: FrontendRegisteredWebhook): Promise<void> {
        this.deletingWebhookId = webhook.hookId;
        try {
            const region = this.model.selectedHub?.region as ERegion;
            await this.model.deleteWebhook(webhook.hookId, region);
            this.registeredWebhooks = this.registeredWebhooks.filter(w => w.hookId !== webhook.hookId);
            this.snackbar("Webhook deleted successfully!", "OK", "is-success");
        } catch (error) {
            this.snackbar("Failed to delete webhook", "OK", "is-danger");
        } finally {
            this.deletingWebhookId = null;
        }
    }

    public async createBluebeamConnection(): Promise<void> {
        if (!this.settings.bluebeamProjectName.trim()) {
            this.snackbar("Please enter a project name.", "OK", "is-warning");
            return;
        }

        if(!this.model.selectedHub?.id || !this.selectedProject?.id || !this.clientID || !this.clientAssetID) return;

        if(!this.settings.bluebeamProjectId) this.settings.bluebeamProjectId = this.defaultBluebeamProjectId;
        
        this.creatingConnection = true;

        try {
            await this.model.createBluebeamConnection({
                connectionId: this.settings.bluebeamProjectId,
                projectName: this.settings.bluebeamProjectName,
                massifClientAssetId: this.clientAssetID,
                massifClientId: this.clientID,
                webhookCallbackUrl: `${process.env.VUE_APP_WEBHOOK_URL}/bluebeamaccess/bluebeam/hooks/${encodeURIComponent(this.clientAssetID)}/${encodeURIComponent(this.selectedProject?.id)}`
                // webhookCallbackUrl: `https://dbfc-122-58-103-163.ngrok-free.app/webhooks/bluebeamaccess/bluebeam/hooks/${encodeURIComponent(this.clientAssetID)}/${encodeURIComponent(this.selectedProject?.id)}`,
            });
            this.isBluebeamConnectionLoaded = true;
            this.$set(this.bluebeamConnectionLoadedMap, this.selectedProject.id, true);
            this.snackbar("Bluebeam connection created!", "OK", "is-success");
            await this.saveSettings();
        } catch (error) {
            console.error("Failed to create connection:", error);
            this.snackbar("Failed to create connection.", "OK", "is-danger");
        } finally {
            this.creatingConnection = false;
        }
    }

    public async bluebeamLogin() {
        if(!this.selectedProject?.id || !this.clientID || !this.clientAssetID || this.hasBluebeamCredentials) return;
        return this.model.bluebeamLogin(`${this.settings.bluebeamProjectId}`)
    }   

    public async autodeskLogin() {
        if(!this.selectedProject?.id || !this.clientID || !this.clientAssetID || this.hasAutodeskCredentials) return;
        return this.model.autodeskLogin(`${this.settings.bluebeamProjectId}`)
    }

    public async disconnectBluebeam() {
        try {
        } catch(err) {
            this.snackbar("Failed to disconnect the Bluebeam account.", "OK", "is-danger");
        } finally {

        }
    }

    public async disconnectAutodesk() {
        try {
        } catch(err) {
            this.snackbar("Failed to disconnect the Autodesk account.", "OK", "is-danger");
        } finally {

        }
    }

    public hasCheckedConnection(projectId: string): boolean {
        if (projectId in this.bluebeamConnectionLoadedMap) {
            return true;
        }
        return false;
    }

    public canDeleteProject(projectId) { 
        return ( 
            !this.isProjectWebhookRegistered(projectId) 
            && !this.bluebeamConnectionLoadedMap[projectId] 
            && !this.bluebeamTokensMap[projectId] 
            && !this.autodeskTokensMap[projectId] 
            && this.hasCheckedConnection(projectId) 
        ); 
    }
    
    public deleteProjectMessage(projectId): string {

        if (!this.hasCheckedConnection(projectId)) 
        { 
            return 'Please click Edit to load the project settings before deleting.'; 
        } 
        
        if (this.isProjectWebhookRegistered(projectId)) 
        { 
            return 'Please remove the webhook before deleting this project settings.'; 
        } 
        
        if (
            this.bluebeamConnectionLoadedMap[projectId] 
            || this.bluebeamTokensMap[projectId]
            || this.autodeskTokensMap[projectId] ) 
        { 
            return 'Please remove the logins and delete the project connection before deleting this project settings.'; 
        } 
        
        return ''; 
    }
}
